import React, { useMemo, useRef, useState } from "react";
import { getCommentPath } from "./commentTreeModel";
import { layoutCommentTree } from "./commentLayout";
import {
  formatCommentTime,
  getCommentDisplayExcerpt,
  getCommentDisplayText,
  getCommentScopeClass,
  getCommentScopeLabel,
} from "./commentText";
import { CommentMapLink, CommentMapPosition, CommentNode } from "./types";

interface CommentMapViewProps {
  roots: CommentNode[];
  byId: Map<number, CommentNode>;
  selectedComment: CommentNode | null;
  selectedCommentId: number | null;
  currentEditId?: number | null;
  onSelect: (commentId: number) => void;
}

interface PanState {
  x: number;
  y: number;
}

function getNodeInitial(comment: CommentNode): string {
  const name = comment.user?.name || "?";
  return name.trim().slice(0, 1).toUpperCase() || "?";
}

function buildLinkPath(from: CommentMapPosition, to: CommentMapPosition): string {
  const startX = from.x + from.width;
  const startY = from.y + from.height / 2;
  const endX = to.x;
  const endY = to.y + to.height / 2;
  const curve = Math.max(72, Math.min(150, (endX - startX) * 0.54));

  return `M ${startX} ${startY} C ${startX + curve} ${startY}, ${endX - curve} ${endY}, ${endX} ${endY}`;
}

function getActiveLinkKey(link: CommentMapLink): string {
  return `${link.fromId}:${link.toId}`;
}

const CommentMapView: React.FC<CommentMapViewProps> = ({
  roots,
  byId,
  selectedComment,
  selectedCommentId,
  currentEditId,
  onSelect,
}) => {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ clientX: number; clientY: number; panX: number; panY: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<PanState>({ x: 28, y: 28 });
  const [isDragging, setIsDragging] = useState(false);

  const layout = useMemo(() => layoutCommentTree(roots), [roots]);
  const positionById = useMemo(() => {
    const result = new Map<number, CommentMapPosition>();
    layout.positions.forEach((position) => result.set(position.id, position));
    return result;
  }, [layout.positions]);

  const selectedPath = useMemo(
    () => getCommentPath(byId, selectedCommentId),
    [byId, selectedCommentId]
  );

  const selectedPathIds = useMemo(
    () => new Set(selectedPath.map((comment) => comment.id)),
    [selectedPath]
  );

  const activeLinkKeys = useMemo(() => {
    const result = new Set<string>();
    for (let index = 0; index < selectedPath.length - 1; index += 1) {
      result.add(`${selectedPath[index].id}:${selectedPath[index + 1].id}`);
    }
    return result;
  }, [selectedPath]);

  const totalComments = layout.positions.length;
  const hasLargeMap = totalComments > 18;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button")) return;
    dragStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const dragStart = dragStartRef.current;
    if (!dragStart) return;

    setPan({
      x: dragStart.panX + event.clientX - dragStart.clientX,
      y: dragStart.panY + event.clientY - dragStart.clientY,
    });
  };

  const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
    dragStartRef.current = null;
    setIsDragging(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture may already be released by the browser.
    }
  };

  const zoomBy = (delta: number) => {
    setZoom((current) => Math.min(1.45, Math.max(0.58, Number((current + delta).toFixed(2)))));
  };

  const resetViewport = () => {
    setZoom(1);
    setPan({ x: 28, y: 28 });
  };

  if (roots.length === 0) {
    return (
      <div className="comment-explorer-map-placeholder">
        <div>
          <h3>No mapped comments</h3>
          <p>Clear search or switch back to All comments to show comment branches in Map mode.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="comment-explorer-map-view">
      <div className="comment-explorer-map-mobile-note">
        Map is available on mobile, but Reader mode is still better for careful reading on small screens.
      </div>

      <div className="comment-explorer-map-toolbar">
        <div className="comment-explorer-map-status">
          <strong>{roots.length}</strong> roots · <strong>{totalComments}</strong> comments
          {selectedComment ? <> · selected: <strong>{getCommentDisplayExcerpt(selectedComment, 38)}</strong></> : null}
        </div>
        <div className="comment-explorer-map-controls" aria-label="Map viewport controls">
          <button type="button" onClick={() => zoomBy(-0.1)} aria-label="Zoom out">−</button>
          <button type="button" onClick={resetViewport}>Reset</button>
          <button type="button" onClick={() => zoomBy(0.1)} aria-label="Zoom in">+</button>
        </div>
      </div>

      <div
        ref={canvasRef}
        className={`comment-explorer-map-canvas ${isDragging ? "is-dragging" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
      >
        <div
          className="comment-explorer-map-stage"
          style={{
            width: layout.width + 96,
            height: layout.height + 96,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          }}
        >
          <svg
            className="comment-explorer-map-links"
            width={layout.width + 96}
            height={layout.height + 96}
            viewBox={`0 0 ${layout.width + 96} ${layout.height + 96}`}
            aria-hidden="true"
          >
            {layout.links.map((link) => {
              const from = positionById.get(link.fromId);
              const to = positionById.get(link.toId);
              if (!from || !to) return null;

              const active = activeLinkKeys.has(getActiveLinkKey(link));
              return (
                <path
                  key={getActiveLinkKey(link)}
                  className={`comment-explorer-map-link ${active ? "is-active" : ""}`}
                  d={buildLinkPath(from, to)}
                />
              );
            })}
          </svg>

          {layout.positions.map((position) => {
            const comment = byId.get(position.id);
            if (!comment) return null;

            const scopeClass = getCommentScopeClass(comment, currentEditId);
            const selected = comment.id === selectedCommentId;
            const inSelectedPath = selectedPathIds.has(comment.id);
            const dimmed = selectedPathIds.size > 0 && !inSelectedPath && !selected;

            return (
              <button
                key={comment.id}
                type="button"
                className={`comment-explorer-map-node ${scopeClass} ${selected ? "is-selected" : ""} ${inSelectedPath ? "is-path" : ""} ${dimmed && hasLargeMap ? "is-dimmed" : ""} ${comment.isRemoved ? "is-removed" : ""}`}
                style={{
                  left: position.x,
                  top: position.y,
                  width: position.width,
                  minHeight: position.height,
                }}
                onClick={() => onSelect(comment.id)}
              >
                <span className="comment-explorer-map-node-kicker">
                  <span className={`comment-explorer-scope-dot ${scopeClass}`} />
                  <span>{getCommentScopeLabel(comment, currentEditId)}</span>
                  <span className="comment-explorer-map-avatar">{getNodeInitial(comment)}</span>
                </span>
                <strong>{comment.isRemoved ? "Removed comment" : getCommentDisplayExcerpt(comment, 58)}</strong>
                <span className="comment-explorer-map-node-text">{selected ? getCommentDisplayText(comment) : getCommentDisplayExcerpt(comment, 104)}</span>
                <span className="comment-explorer-map-node-foot">
                  <span>{formatCommentTime(comment.createdAt)}</span>
                  <span>{comment.directReplyCount} direct · {comment.totalReplyCount} total</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CommentMapView;
